import { AigcSwitch } from "@/components/AigcSwitch";
import BoardSelect from "@/components/BoardSelect";
import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { deletePin, getGrocery, getPin, getTags, updatePin } from "@/services";
import { getContentType } from "@/utils/getContentType";
import imageUrl from "@/utils/imageUrl";
import useReq from "@/utils/useReq";
import { Button, Form } from "antd";
import { isNil, omit, omitBy } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { When } from "react-if";
import HBInput from "../../HBInput";
import HBSelect from "../../HBInput/select";
import HBLoading from "../../HBLoading";
import message from "../../HBMessage";
import HBConfirm from "../../HBModal/confirm";
import HBTagInput from "../../HBTagInput";
import styles from "../index.module.less";

/**
 *
 * @param {number} props.pinId
 * @param {() => void =} props.onCancel
 * @param {(pin: Object, board: Board) => void =} props.onOk
 * @param {() => any =} props.onDelete
 * @param {(canBeSeen: boolean) => void =} props.setCanBeSeen 控制当前弹框显示隐藏
 * @returns {JSX.Element}
 */
export default function PinEditor(props) {
  const formRef = useRef();
  const { pinId, onDelete, onCancel, onOk, setCanBeSeen } = props;
  const [loading, setLoading] = useState(true);
  const [pin, setRecord] = useState();
  const [initData, setData] = useState({});
  const { user } = useAppStore(({ user }) => ({
    user,
  }));

  const isOwner = (pin?.user?.user_id || pin?.user_id) === user?.user_id;

  const pinImageUrl = useMemo(
    () => (pin?.file ? imageUrl(pin.file, { width: 400 }) : ""),
    [pin]
  );

  // 从 pin.file_aigc 来。 pin.file_aigc === 0 疑似时也可以操作
  const [originAigc, setOriginAigc] = useState(undefined);
  const [isAigc, setIsAigc] = useState(false);
  const isAigcChangeable = !originAigc; // 是否可以修改aigc 如果已有 aigc 则不可修改 不显示 aigc Checkbox

  const onValuesChange = useCallback((changedValues) => {
    const tags = changedValues.tags;
    if (tags && tags?.length > 10) {
      message.info("最多添加10个标签");
      formRef.current.setFieldsValue({ tags: tags.slice(0, 10) });
    }

    if ("is_aigc" in changedValues) {
      setIsAigc(changedValues.is_aigc);
    }
  }, []);

  const onDeleteClick = useCallback(() => {
    const show = (visible) => setCanBeSeen && setCanBeSeen(visible);
    show(false);
    HBConfirm.open({
      title: "删除采集",
      content: "确定要删除当前采集？",
      okText: "确定",
      cancelText: "取消",
      okButtonProps: {
        "data-gd-click": "button_click",
        "data-button-name": "确认删除采集",
        "data-content-source": "采集编辑页",
        "data-content-id": pinId,
        "data-board-id": pin?.board_id,
        "data-file-id": pin?.file_id,
        "data-material-id":
          pin?.file_material?.material_id || pin?.extra?.material_id,
        "data-unique-id": pin?.pin_id,
        "data-content-type": getContentType(pin),
      },
      onOk: async () => {
        const { err } = await deletePin({ pin_id: pinId });
        if (err) {
          return;
        }
        message.success("删除成功");
        onDelete && requestAnimationFrame(() => onDelete());
      },
      onCancel: () => {
        show(true);
      },
    });
  }, [pinId, onDelete, setCanBeSeen, pin]);

  const onSubmit = useCallback(
    async (values) => {
      if (!values.board_id) {
        message.error("画板不能为空");
        return;
      }

      // 如果有 is_aigc 则必须有值
      if (values.is_aigc) {
        values.aigc = omitBy(values.aigc || {}, (v) => !v);
        values.aigc = {
          ...(values.aigc || {}),
          prompt: values.aigc?.prompt || "",
        };
      } else if (values.aigc) {
        values.aigc = omitBy(values.aigc, (v) => !v);
        // {} 空对象时不传
        if (Object.keys(values.aigc).length === 0) {
          values.aigc = undefined;
        }
      }

      // setLoading(true);
      // isOwner时，删除不可修改的字段
      const result = await updatePin({
        ...omit(values, isOwner ? [] : ["tags"]),
        pin_id: pin?.pin_id,
      });

      if (!result.err) {
        message.success(result?.msg || "修改成功");
        onOk &&
          requestAnimationFrame(async () => {
            if (pin?.board_id !== result?.pin?.board_id) {
              window.dispatchEvent(
                new CustomEvent(EVENT.CHANGE_PIN_BOARD, {
                  detail: {
                    pinId: pin?.pin_id,
                    boardId: result?.pin?.board_id,
                    originBoardId: pin?.board_id,
                  },
                })
              );
              onOk(result.pin);
            } else {
              onOk({
                ...result.pin,
                board: {
                  ...pin?.board,
                  is_collab_member: pin?.is_collab_member,
                },
              });
            }
          });
      }
      setLoading(false);
    },
    [onOk, pin]
  );

  const init = useCallback(async () => {
    setLoading(true);
    const { pin, err } = await getPin({ pin_id: pinId });
    if (err) {
      setLoading(false);
      onCancel && requestAnimationFrame(onCancel);
      return;
    }
    setRecord(pin);
    setOriginAigc(pin.extra?.aigc);
    setIsAigc(!isNil(pin.extra?.aigc));
    setData({
      board_id: pin.board_id,
      text: pin.raw_text,
      link: pin.link || "",
      tags: pin.tags,
      is_aigc: !isNil(pin.extra?.aigc),
      aigc: {
        aigc_category: pin.file_aigc?.aigc_category,
        prompt: pin.file_aigc?.prompt,
        negativePrompt: pin.file_aigc?.extra?.negativePrompt,
        model: pin.file_aigc?.model,
      },
    });
    setLoading(false);
  }, [onCancel, pinId]);

  const appendBoardList = useMemo(() => {
    if (pin?.board?.is_private === 2) {
      return [pin?.board];
    }

    return [];
  }, [pin]);

  useEffect(() => {
    init();
  }, [init]);

  const { data: aigcCategoryOptions } = useReq(async () => {
    const res = await getGrocery("pin_detail_ai_params");

    res.linkMap.push({
      name: "其他",
      value: "other",
      isShow: true,
      link: "",
    });

    return res.linkMap.filter((s) => s.isShow);
  });

  const { data: tagResult } = useReq(
    async () => {
      const urlname = user?.urlname;
      let tagResult = await getTags({ urlname, sort: "often" });
      tagResult.recent = tagResult?.recent.map((item) => item.tag_name) || [];

      return tagResult;
    },
    {
      refreshDeps: [user?.urlname],
    }
  );

  return (
    <div className={styles.editor}>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <HBLoading size={"30px"} />
        </div>
      ) : (
        <>
          <div className={styles.left}>
            <div className={styles.emptyHolder} />
            <div className={styles.pinImage}>
              <img
                className="transparent-img-bg"
                src={pinImageUrl}
                alt={"采集缩略图"}
              />
            </div>
          </div>
          <div className={styles.right}>
            <div className={styles.title}>编辑采集</div>
            <Form
              ref={formRef}
              autoComplete="off"
              onFinish={onSubmit}
              initialValues={initData}
              onValuesChange={onValuesChange}
              className={styles.form}
            >
              <Form.Item name="text">
                <HBInput
                  placeholder="描述"
                  type="textarea"
                  autoSize
                  autoFocus
                />
              </Form.Item>

              <Form.Item name={"tags"} className={styles.hbTagContent}>
                <HBTagInput
                  suggestVisible={true}
                  suggestTags={tagResult?.recent}
                  allTags={tagResult?.tags}
                  placeholder={"标签"}
                  useLabel={true}
                  disabled={!isOwner}
                />
              </Form.Item>
              <Form.Item name={"link"} className={styles.linkContent}>
                <HBInput
                  placeholder={"来源网站链接"}
                  disabled={
                    !!pin?.original ||
                    (!!pin?.via_client &&
                      (pin?.via_client?.via_id === 95 ||
                        pin?.via_client?.via_id === 7))
                  }
                />
              </Form.Item>

              <Form.Item name="board_id">
                <BoardSelect
                  placeholder="选择画板"
                  useLabel={true}
                  emptyBoardsExpand={false}
                  initEmpty={true}
                  appendBoardList={appendBoardList}
                  useCreation={pin?.board?.extra?.is_creation}
                  onlyCreation={pin?.board?.extra?.is_creation}
                  disabled={!isOwner}
                />
              </Form.Item>

              <When condition={!pin?.original}>
                <Form.Item name="is_aigc">
                  <AigcSwitch
                    className={styles.aigcSwitch}
                    disabled={!isAigcChangeable}
                    originAigc={originAigc}
                  />
                </Form.Item>
                <When condition={isAigc}>
                  <Form.Item name={["aigc", "aigc_category"]}>
                    <HBSelect
                      placeholder="选择创作工具"
                      options={aigcCategoryOptions}
                      optLabel="name"
                      disabled={!!initData.aigc?.aigc_category}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item name={["aigc", "model"]}>
                    <HBInput
                      placeholder="模型"
                      disabled={!!initData.aigc?.model}
                    />
                  </Form.Item>
                  <Form.Item
                    name={["aigc", "prompt"]}
                    // rules={[
                    //   {
                    //     required: true,
                    //     message: <ErrMsg>AI作品必须填写Prompt</ErrMsg>,
                    //   },
                    // ]}
                  >
                    <HBInput
                      placeholder="添加提示词(Prompt)"
                      type="textarea"
                      rows={3}
                      disabled={!!initData.aigc?.prompt}
                    />
                  </Form.Item>
                  <Form.Item name={["aigc", "negativePrompt"]}>
                    <HBInput
                      placeholder="添加负提示词(Negative Prompt)"
                      type="textarea"
                      rows={3}
                      disabled={!!initData.aigc?.negativePrompt}
                    />
                  </Form.Item>
                </When>
              </When>
              <Form.Item className={styles.actions}>
                <div className={styles.actionsWrap}>
                  <div></div>
                  <div className={styles.leftActions}>
                    <Button
                      shape="round"
                      onClick={onDeleteClick}
                      data-gd-click="button_click"
                      data-button-name="删除采集"
                      data-content-id={pin?.pin_id}
                      data-board-id={pin?.board_id}
                      data-file-id={pin?.file_id}
                      data-material-id={pin?.file_material?.material_id}
                      data-unique-id={pin?.pin_id}
                      data-content-type={getContentType(pin)}
                    >
                      删除采集
                    </Button>
                    <Button type={"primary"} shape="round" htmlType="submit">
                      确认
                    </Button>
                  </div>
                </div>
              </Form.Item>
            </Form>
          </div>
        </>
      )}
    </div>
  );
}
